import { type ApprovalPolicy, approvalDocTypeSchema } from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  Dialog,
  EmptyState,
  FormField,
  Select,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ListChecks, Plus } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Amount } from "../../../features/accounting/shared.js";
import { PolicyForm } from "../../../features/approvals/policy-form.js";
import { useApprovalPolicies, useRefreshApprovals } from "../../../features/approvals/queries.js";
import {
  ApprovalsPage,
  docTypeLabel,
  useApprovalPermissions,
  useApprovalsCrumb,
} from "../../../features/approvals/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";

const searchSchema = z.object({ docType: approvalDocTypeSchema.optional().catch(undefined) });

export const Route = createFileRoute("/_app/onaylar/politikalar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: PoliciesPage,
});

function PoliciesPage(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const crumb = useApprovalsCrumb();
  const permissions = useApprovalPermissions();
  const refresh = useRefreshApprovals();
  const [editing, setEditing] = useState<{ policy: ApprovalPolicy | null } | null>(null);
  const [deleting, setDeleting] = useState<ApprovalPolicy | null>(null);

  const policies = useApprovalPolicies(
    { ...(search.docType ? { docType: search.docType } : {}), includeInactive: true },
    permissions.canRead,
  );

  const remove = useMutation({
    mutationFn: async (id: string) => api.DELETE("/approval-policies/{id}", { params: { path: { id } } }),
    onSuccess: async () => {
      setDeleting(null);
      await refresh();
      toast({ tone: "success", title: t("approvals.policies.deleted") });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  const columns = useMemo<DataTableColumn<ApprovalPolicy>[]>(
    () => [
      {
        id: "docType",
        header: t("approvals.fields.docType"),
        hideable: false,
        cell: (row) => docTypeLabel(row.docType),
      },
      {
        id: "name",
        header: t("approvals.fields.policyName"),
        hideable: false,
        cell: (row) => (
          <span className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="font-medium underline underline-offset-4"
              onClick={() => setEditing({ policy: row })}
            >
              {row.name}
            </button>
            {row.isSystemDefault ? (
              <StatusBadge tone="info">{t("approvals.policies.systemDefault")}</StatusBadge>
            ) : null}
          </span>
        ),
      },
      {
        id: "conditions",
        header: t("approvals.policies.conditions"),
        cell: (row) => (
          <span className="text-xs text-muted-foreground">
            {row.companyName ?? t("approvals.fields.allCompanies")}
            {" · "}
            {row.stationIds.length > 0
              ? t("approvals.policies.stationCount", { count: row.stationIds.length })
              : t("approvals.policies.allStations")}
            {row.minAmount !== null || row.maxAmount !== null ? (
              <>
                {" · "}
                {row.minAmount === null ? "—" : <Amount value={row.minAmount} />}
                {" … "}
                {row.maxAmount === null ? "—" : <Amount value={row.maxAmount} />}
              </>
            ) : null}
          </span>
        ),
      },
      {
        id: "stepCount",
        header: t("approvals.policies.stepCount"),
        align: "center",
        cell: (row) => String(row.steps.length),
      },
      {
        id: "priority",
        header: t("approvals.fields.priority"),
        align: "center",
        cell: (row) => String(row.priority),
      },
      {
        id: "isActive",
        header: t("common.status"),
        hideable: false,
        cell: (row) => (
          <StatusBadge tone={row.isActive ? "success" : "neutral"}>
            {row.isActive ? t("common.active") : t("common.inactive")}
          </StatusBadge>
        ),
      },
      {
        id: "actions",
        header: t("common.actions"),
        align: "right",
        cell: (row) =>
          permissions.canManagePolicy ? (
            <Button variant="ghost" size="sm" onClick={() => setDeleting(row)}>
              {t("common.delete")}
            </Button>
          ) : null,
      },
    ],
    [t, permissions.canManagePolicy],
  );

  if (!permissions.canRead)
    return (
      <ApprovalsPage
        title={t("approvals.policies.title")}
        breadcrumb={[crumb, { label: t("approvals.policies.title") }]}
      >
        <Alert tone="info">{t("approvals.policies.noPermission")}</Alert>
      </ApprovalsPage>
    );

  return (
    <ApprovalsPage
      title={t("approvals.policies.title")}
      description={t("approvals.policies.description")}
      breadcrumb={[crumb, { label: t("approvals.policies.title") }]}
      actions={
        permissions.canManagePolicy ? (
          <Button onClick={() => setEditing({ policy: null })}>
            <Plus aria-hidden="true" />
            {t("approvals.policies.new")}
          </Button>
        ) : null
      }
    >
      <div className="grid gap-4">
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">{t("approvals.policies.autoApproveHint")}</p>
        </Card>
        <DataTable
          caption={t("approvals.policies.title")}
          columns={columns}
          rows={policies.data ?? []}
          rowKey={(row) => row.id}
          loading={policies.isPending}
          error={policies.error ? <Alert tone="danger">{errorMessage(policies.error)}</Alert> : undefined}
          emptyState={
            <EmptyState
              headingLevel={2}
              icon={<ListChecks />}
              title={t("approvals.policies.emptyTitle")}
              description={t("approvals.policies.autoApproveHint")}
            />
          }
          filters={
            <FormField label={t("approvals.fields.docType")} className="w-48">
              {(control) => (
                <Select
                  {...control}
                  value={search.docType ?? ""}
                  onChange={(event) =>
                    void navigate({
                      search: event.target.value
                        ? { docType: approvalDocTypeSchema.parse(event.target.value) }
                        : {},
                      replace: true,
                    })
                  }
                >
                  <option value="">{t("common.all")}</option>
                  {approvalDocTypeSchema.options.map((type) => (
                    <option key={type} value={type}>
                      {docTypeLabel(type)}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
          }
        />
      </div>

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        title={editing?.policy ? t("approvals.policies.edit") : t("approvals.policies.new")}
        className="max-w-3xl"
      >
        {editing ? (
          <div className="max-h-[70vh] overflow-y-auto pr-1">
            <PolicyForm
              policy={editing.policy}
              readOnly={!permissions.canManagePolicy}
              onDone={() => setEditing(null)}
              onCancel={() => setEditing(null)}
            />
          </div>
        ) : null}
      </Dialog>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        tone="danger"
        title={t("approvals.policies.deleteTitle")}
        description={t("approvals.policies.deleteDescription", {
          docType: deleting ? docTypeLabel(deleting.docType) : "",
        })}
        confirmLabel={t("common.delete")}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id);
        }}
      />
    </ApprovalsPage>
  );
}
